/**
 * Allowed Users Management Page
 * Displays all users who are allowed to login to the system
 */

import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { 
  getAllowedUsers, 
  getAllowedUsersByRole, 
  getAllowedUsersByOrganization,
  getAllowedUserEmails,
  exportAllowedEmailsList
} from '../utils/allowedUsers';
import { User, UserRole, AppState } from '../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Download, 
  Search, 
  Users, 
  Building2, 
  Shield,
  Mail,
  ArrowLeft,
  CheckCircle2,
  Copy,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../providers/AppProvider';
import UserContactLinks from '../components/UserContactLinks';
import { getUserProfilePath } from '../utils/userProfile';
import { getRoleColors } from '../constants/colorScheme';

const AllowedUsers: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  const { state } = useApp();
  
  const allUsers = useMemo(() => getAllowedUsers(), []);
  const usersByRole = useMemo(() => getAllowedUsersByRole(), []);
  const usersByOrg = useMemo(() => getAllowedUsersByOrganization(), []);
  const allowedEmails = useMemo(() => getAllowedUserEmails(), []);

  if (!state) {
    return <div className="container mx-auto p-6 max-w-7xl text-muted-foreground">Loading allowed users...</div>;
  }
  

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return allUsers;
    
    const query = searchQuery.toLowerCase();
    return allUsers.filter(user => 
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      user.role.toLowerCase().includes(query)
    );
  }, [allUsers, searchQuery]);

  const handleExportList = () => {
    const content = exportAllowedEmailsList();
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'allowed-users.md';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Allowed users list exported');
  };

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email);
    setCopiedEmail(email);
    toast.success('Email copied to clipboard');
    setTimeout(() => setCopiedEmail(null), 2000);
  };

  const handleCopyAllEmails = () => {
    const emailsList = allowedEmails.join('\n');
    navigator.clipboard.writeText(emailsList);
    toast.success(`${allowedEmails.length} emails copied to clipboard`);
  };

  const getRoleBadgeColor = (role: UserRole): string => {
      return getRoleColors(role).badge;
  };

  const UserCard: React.FC<{ user: User }> = ({ user }) => {
    const org = state?.organizations.find(o => o.id === user.organizationId);
    const profilePath = getUserProfilePath(user, state?.currentUser);
    
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback>{user.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
            </Avatar>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold truncate">{user.name}</h3>
                <Badge className={`border ${getRoleBadgeColor(user.role)} text-xs`}>
                  {user.role.replace('_', ' ')}
                </Badge>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Mail className="h-3 w-3" />
                <span className="truncate flex-1">{user.email}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => handleCopyEmail(user.email)}
                >
                  {copiedEmail === user.email ? (
                    <Check className="h-3 w-3 text-chart-2" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
              
              {org && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Building2 className="h-3 w-3" />
                  <span>{org.name}</span>
                </div>
              )}

              <UserContactLinks user={user} className="mt-2" />
              <Link
                to={profilePath}
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:underline"
              >
                View profile
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
              <Shield className="h-8 w-8 text-chart-2" />
              Allowed Users
            </h1>
            <p className="text-muted-foreground">
              Users authorized to access the project management system
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCopyAllEmails}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy All Emails
            </Button>
            <Button
              onClick={handleExportList}
            >
              <Download className="h-4 w-4 mr-2" />
              Export List
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{allUsers.length}</div>
              <p className="text-xs text-muted-foreground">Allowed to login</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Organizations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Object.keys(usersByOrg).length}</div>
              <p className="text-xs text-muted-foreground">With active users</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Shield className="h-4 w-4" />
                User Roles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Object.keys(usersByRole).length}</div>
              <p className="text-xs text-muted-foreground">Different roles</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* User Lists */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="all">
            All Users ({allUsers.length})
          </TabsTrigger>
          <TabsTrigger value="by-role">
            By Role ({Object.keys(usersByRole).length})
          </TabsTrigger>
          <TabsTrigger value="by-org">
            By Organization ({Object.keys(usersByOrg).length})
          </TabsTrigger>
        </TabsList>

        {/* All Users Tab */}
        <TabsContent value="all">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredUsers.length > 0 ? (
              filteredUsers.map(user => (
                <UserCard key={user.id} user={user} />
              ))
            ) : (
              <Card className="col-span-full">
                <CardContent className="p-8 text-center text-muted-foreground">
                  No users found matching your search.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* By Role Tab */}
        <TabsContent value="by-role">
          <div className="space-y-6">
            {Object.entries(usersByRole)
              .sort(([roleA], [roleB]) => roleA.localeCompare(roleB))
              .map(([role, users]) => (
                <div key={role}>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Badge className={`border ${getRoleBadgeColor(role as UserRole)}`}>
                      {role.replace('_', ' ')}
                    </Badge>
                    <span className="text-muted-foreground text-sm">
                      ({users.length} {users.length === 1 ? 'user' : 'users'})
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {users.map(user => (
                      <UserCard key={user.id} user={user} />
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </TabsContent>

        {/* By Organization Tab */}
        <TabsContent value="by-org">
          <div className="space-y-6">
            {Object.entries(usersByOrg).map(([orgId, users]) => {
              const org = state.organizations.find(o => o.id === orgId);
              return (
                <div key={orgId}>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    {org?.name || orgId}
                    <span className="text-muted-foreground text-sm">
                      ({users.length} {users.length === 1 ? 'user' : 'users'})
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {users.map(user => (
                      <UserCard key={user.id} user={user} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Info Card */}
      <Card className="mt-6 border-primary/40 bg-primary/10">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            Access Control Information
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            • Only users listed on this page can sign in to the system using Google Authentication.
          </p>
          <p>
            • Users are automatically added to the allowed list when they are added to projects as team members or stakeholders.
          </p>
          <p>
            • If a user attempts to sign in with an email not on this list, they will be denied access and signed out automatically.
          </p>
          <p>
            • To add new users, add them to the global users list or assign them to project teams.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AllowedUsers;
